import React, { useState, useEffect, useCallback } from "react";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiUploadCloud,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { http } from "../../axios/axios";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";

const API_BASE = import.meta.env.VITE_API_BASE || "http://localhost:8080";

const PartnerManager = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPartner, setCurrentPartner] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // --- Pagination & Search State ---
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 8;

  // 1. GET ALL (With Search & Pagination)
  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const res = await http.get(
        `/partners?search=${search}&page=${page}&limit=${limit}`,
      );
      // Accessing data based on your specific Controller response structure
      setPartners(res.data.data);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error("Intelligence retrieval failed", err);
    } finally {
      setLoading(false);
    }
  }, [search, page, limit]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchPartners();
    }, 400); // Slight delay to prevent API spamming
    return () => clearTimeout(delayDebounceFn);
  }, [fetchPartners]);

  // 2. CREATE & UPDATE (Integrated into handleSave)
  const handleSavePartner = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.currentTarget);

    // Only append logo if a new file was actually selected
    if (selectedFile) {
      formData.append("logo", selectedFile);
    }

    try {
      if (currentPartner) {
        // PUT API: Updates text and handles old image replacement on server
        await http.put(`/partners/${currentPartner._id}`, formData);
        toast.success("Partner Entry Updated");
      } else {
        // POST API: Creates new partner entry
        await http.post("/partners", formData);
        toast.success("New Partner Entry Created");
      }
      fetchPartners();
      closeModal();
    } catch (err) {
      console.error("Authorization failed", err);
    } finally {
      setLoading(false);
    }
  };

  // 3. DELETE (Handles DB entry and File removal)
  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Terminate this partner alliance? Physical logs (images) will be wiped from storage.",
      )
    ) {
      try {
        await http.delete(`/partners/${id}`);
        toast.success("Partner Entry Terminated");
        fetchPartners();
      } catch (err) {
        console.error("Termination failed", err);
      }
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const openModal = (partner = null) => {
    setCurrentPartner(partner);
    if (partner?.logo) {
      // Handles your relative pathing 'uploads/filename.jpg'
      setPreviewImage(`${API_BASE}/${partner.logo}`);
    } else {
      setPreviewImage(null);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentPartner(null);
    setSelectedFile(null);
    setPreviewImage(null);
  };

  return (
    <div className="p-4 md:p-8 space-y-6 min-h-screen text-slate-300 font-sans">
      {/* HEADER & SEARCH BAR */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-black text-white uppercase tracking-tighter italic">
            Alliance <span className="text-blue-500">Network.</span>
          </h2>
          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-[0.3em]">
            Partner Ecosystem v4.5
          </p>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-grow md:w-64">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Search Intelligence..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1); // Reset to page 1 on new search
              }}
              className="w-full bg-[#0a0f1a] border border-white/5 rounded-xl py-2 pl-10 pr-4 text-xs focus:border-blue-500 outline-none transition-all"
            />
          </div>
          <button
            onClick={() => openModal()}
            className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shrink-0 shadow-lg shadow-blue-900/20"
          >
            <FiPlus className="inline mr-2" /> Initialize
          </button>
        </div>
      </div>

      {/* PARTNER TABLE */}
      <div className="bg-[#0a0f1a] rounded-3xl border border-white/5 overflow-hidden shadow-2xl">
        <table className="w-full text-left">
          <thead className="bg-white/[0.02] border-b border-white/5 uppercase text-[9px] font-black text-slate-500 tracking-widest">
            <tr>
              <th className="px-6 py-4">Brand Identity</th>
              <th className="px-6 py-4">Database Protocol</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading && partners.length === 0 ? (
              <tr>
                <td colSpan="3" className="py-20 text-center">
                  <Loader2 className="animate-spin mx-auto text-blue-500" />
                </td>
              </tr>
            ) : partners.length === 0 ? (
              <tr>
                <td
                  colSpan="3"
                  className="py-20 text-center text-slate-600 uppercase text-[10px] font-bold tracking-widest"
                >
                  No matching records found.
                </td>
              </tr>
            ) : (
              partners.map((partner) => (
                <tr
                  key={partner._id}
                  className="hover:bg-white/[0.01] transition-colors group"
                >
                  <td className="px-6 py-4 flex items-center gap-4">
                    <div className="w-14 h-10 rounded-lg bg-white p-1 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center shadow-inner">
                      <img
                        src={`${API_BASE}/${partner.logo}`}
                        className="max-w-full max-h-full object-contain grayscale group-hover:grayscale-0 transition-all duration-500"
                        alt={partner.name}
                      />
                    </div>
                    <p className="text-white font-bold text-xs uppercase tracking-tight italic">
                      {partner.name}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-[10px] font-mono text-slate-500 uppercase">
                    ID: {partner._id.slice(-6)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openModal(partner)}
                        className="p-2 bg-white/5 hover:bg-blue-600 rounded-lg transition-all text-slate-400 hover:text-white"
                      >
                        <FiEdit2 size={12} />
                      </button>
                      <button
                        onClick={() => handleDelete(partner._id)}
                        className="p-2 bg-white/5 hover:bg-red-600 rounded-lg transition-all text-slate-400 hover:text-white"
                      >
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* PAGINATION */}
        <div className="p-4 border-t border-white/5 flex items-center justify-between bg-white/[0.01]">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
            Entry {partners.length > 0 ? (page - 1) * limit + 1 : 0} -{" "}
            {Math.min(page * limit, totalPages * limit)}
          </p>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="p-2 bg-white/5 rounded-lg disabled:opacity-20 hover:bg-white/10 transition-all text-white"
            >
              <FiChevronLeft size={16} />
            </button>
            <button
              disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage((p) => p + 1)}
              className="p-2 bg-white/5 rounded-lg disabled:opacity-20 hover:bg-white/10 transition-all text-white"
            >
              <FiChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* MODAL (FORM) */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="fixed inset-0 bg-black/95 backdrop-blur-md"
            />
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              className="bg-[#0a0f1a] border border-white/10 w-full max-w-lg rounded-[2.5rem] overflow-hidden relative z-10 shadow-2xl"
            >
              <form onSubmit={handleSavePartner} className="p-8 space-y-6">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-black uppercase tracking-widest text-white italic">
                    {currentPartner ? "Modify Alliance" : "New Authorization"}
                  </h3>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="text-slate-500 hover:text-white"
                  >
                    <FiX size={20} />
                  </button>
                </div>

                {/* Logo Upload Box */}
                <div className="h-32 bg-white border-2 border-dashed border-slate-200 rounded-3xl relative overflow-hidden flex flex-col items-center justify-center group hover:border-blue-500 transition-all cursor-pointer">
                  {previewImage ? (
                    <img
                      src={previewImage}
                      className="max-h-[80%] object-contain"
                      alt="Preview"
                    />
                  ) : (
                    <>
                      <FiUploadCloud size={24} className="text-slate-400" />
                      <span className="text-[8px] font-black uppercase mt-2 text-slate-400">
                        Upload Logo
                      </span>
                    </>
                  )}
                  <input
                    type="file"
                    multiple={false}
                    accept="image/*"
                    onChange={handleImageChange}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </div>

                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1">
                      Brand Name
                    </label>
                    <input
                      name="name"
                      defaultValue={currentPartner?.name}
                      placeholder="e.g. DAHUA TECHNOLOGY"
                      className="w-full bg-black border border-white/10 rounded-xl p-3 text-xs font-bold text-white outline-none focus:border-blue-600 transition-colors"
                      required
                    />
                  </div>
                </div>

                <button
                  disabled={loading}
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-500 py-4 rounded-xl text-[10px] font-black uppercase tracking-[0.4em] transition-all flex justify-center items-center gap-2 shadow-lg shadow-blue-900/30"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : currentPartner ? (
                    "Update Protocol"
                  ) : (
                    "Authorize Entry"
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PartnerManager;
